import { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import styles from './Checkout.module.css';
import Header from '../.shared/header/header';
import Footer from '../.shared/footer/footer';
import { useInterview } from '../../utils/interviewContext';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { identifyPostHogUser } from '../../utils/posthogUtils';
import usePopUp from '../../hooks/usePopUp';
import { SourceContext } from '../../utils/sourceContext';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { CircularProgress } from '@mui/material';
import GradientCheckBox from '../.shared/GradientCheckBox/GradientCheckBox';
import Checkbox from '../.shared/CheckBox/CheckBox';
import SubscriptionInfo from './SubscriptionInfo';
import InputText from '../.shared/InputText/InputText';
import InputAutocomplete from '../.shared/InputAutocomplete/InputAutocomplete';
import PaymentWarningPopUp from '../.shared/PaymentWarningPopUp/PaymentWarningPopUp';
import NonWholesaleStateWarningPopUp from '../.shared/NonWholesaleStateWarningPopUp/NonWholesaleStateWarningPopUp';
import Pricing from '../../data/Pricing';
import { US_STATES } from '../../data/usStates';
import LegitScriptBanner from '../.shared/LegitScriptBanner/LegitScriptBanner';
import BackorderBanner from './BackorderBanner';
import { useFeatureFlagEnabled } from 'posthog-js/react';

const NON_SWITCH_AND_SAVE_STATES = (() => {
    try { return JSON.parse(process.env.REACT_APP_NON_SWITCH_AND_SAVE_STATES || '[]'); }
    catch { return []; }
})();

// Switched off in PostHog the moment sublingual stock returns — no deploy needed.
const SUBLINGUAL_BACKORDER_FLAG = 'sublingual-backorder';


function CheckoutContent() {

    const pricing = Pricing();
    const stripe = useStripe();
    const elements = useElements();
    const { getInterviewSummary } = useInterview();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { source, utmMedium, utmCampaign, utmContent, utmTerm } = useContext(SourceContext);

    // State for the selected plan ID
    const initialPlanId = localStorage.getItem('plan_id') || pricing.getPricingOptionsByProductName('semaglutide')['suspension']['monthly']['plan_id']; // If there's no planId, fallback to Semaglutide suspension monthly
    const [planId, setPlanId] = useState(initialPlanId);

    // Initial plan details
    const initialPlan = pricing.getPlanById(initialPlanId);

    // Sublingual (suspension) products are back-ordered and cannot be fulfilled,
    // so the order is blocked while the flag is on. Derived from planId, so it
    // clears by itself if the visitor picks a different plan.
    //
    // The hook returns undefined until flags load, and stays undefined when
    // PostHog is unreachable or blocked by an ad blocker. Comparing with === true
    // fails open, so an outage can never stop legitimate orders once stock is
    // back; swap to !== false to fail closed instead.
    const backorderActive = useFeatureFlagEnabled(SUBLINGUAL_BACKORDER_FLAG) === true;

    // getPlanById returns null for an unknown id, which happens today when there
    // is no stored plan_id, so the formulation read must be optional.
    const currentPlan = pricing.getPlanById(planId);
    const sublingualBlocked = backorderActive && currentPlan?.formulation === 'suspension';

    // State for form fields
    const [formData, setFormData] = useState({
        firstName: '',
        middleInitial: '',
        lastName: '',
        email: '',
        cell: '',
        billingAddressLine1: '',
        billingAddressLine2: '',
        billingCity: '',
        billingState: '',
        billingZipCode: '',
        shippingAddressLine1: '',
        shippingAddressLine2: '',
        shippingCity: '',
        shippingState: '',
        shippingZipCode: '',
        sameAsShipping: false,
        cardholderName: '',
        notificationOptin: true,
        marketingOptin: true,
        telemedicineOptin: true,
        consentAcknowledged: false
    });
    const [phoneDisplayValue, setPhoneDisplayValue] = useState('');
    const [coupon] = useState(null); // Not used but kept for consistency

    // Error states
    const [cardErrorMessage, setCardErrorMessage] = useState('');
    const [formErrors, setFormErrors] = useState({});

    // UI States and refs
    const [cardInputFocused, setCardInputFocused] = useState(false);
    const contactInfoRef = useRef();
    const shippingAddressRef = useRef();
    const billingAddressRef = useRef();
    const paymentInfoRef = useRef();
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Payment warning states
    const [isPaymentWarningOpen, openPaymentWarning, closePaymentWarning] = usePopUp(false);
    const [telegraUrl, setTelegraUrl] = useState('');

    // Non-wholesale state warning states
    const [isStateWarningOpen, openStateWarning, closeStateWarning] = usePopUp(false);
    const [restrictedStateAbbr, setRestrictedStateAbbr] = useState('');

    // Handler for subscription info component
    const handleChangeFrequency = (newPlanId) => {
        setPlanId(newPlanId);
    }

    // Handlers for non-wholesale state warning popup
    const handleStateWarningConfirm = () => {
        if (planId) {
            const retailPrices = pricing.getRetailPrices();
            const currentPlan = pricing.getPlanById(planId);
            const productKey = currentPlan.product.toLowerCase();
            const formKey = currentPlan.formulation;
            const periodKey = currentPlan.subscription_length === '1 Month' ? 'monthly' : 'threeMonths';
            const retailPlan = retailPrices[productKey]?.[formKey]?.[periodKey];
            if (retailPlan) {
                localStorage.setItem('plan_id', retailPlan.id);
            }
        }

        localStorage.setItem('tb2_user_state', JSON.stringify({
            state: restrictedStateAbbr,
            cachedAt: new Date().toISOString()
        }));

        window.location.reload();
    };

    const handleStateWarningCancel = () => {
        setFormData(prevData => ({ ...prevData, shippingState: '' }));
        closeStateWarning();
    };

    // Handler for form field changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'telemedicineOptin') {
            setFormErrors(prevErrors => ({
                ...prevErrors,
                telemedicineOptin: checked ? '' : 'You must acknowledge telemedicine enrollment before placing your order.'
            }));
        }

        setFormData(prevData => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        localStorage.setItem('checkout_form_data', JSON.stringify({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (NON_SWITCH_AND_SAVE_STATES && NON_SWITCH_AND_SAVE_STATES.length > 0 && name === 'shippingState' && pricing.isWholesale() && NON_SWITCH_AND_SAVE_STATES.includes(value)) {
            setRestrictedStateAbbr(value);
            openStateWarning();
        }

    }

    // Handler for clearing error messages on focus
    const handleClearError = (e) => {
        const { name } = e.target;
        setFormErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    }

    // Validation helpers
    const validateFirstName = () => {
        if (!formData.firstName || formData.firstName.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, firstName: 'First name is required.' }));
            return false;
        }
        return true;
    }

    const validateLastName = () => {
        if (!formData.lastName || formData.lastName.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, lastName: 'Last name is required.' }));
            return false;
        }
        return true;
    }

    const validateEmail = () => {
        const emailRegex = /^(([^\s()<>[\]\\.,;:\s@\"]+(\.[^\s()<>[\]\\.,;:\s@\"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!formData.email || formData.email.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, email: 'Email is required.' }));
            return false;
        } else if (!emailRegex.test(formData.email)) {
            setFormErrors(prevErrors => ({ ...prevErrors, email: 'Invalid email format.' }));
            return false;
        }
        return true;
    }

    const validatePhone = () => {
        const phoneDigits = formData.cell.replace(/\D/g, '');
        if (!formData.cell || formData.cell.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, cell: 'Phone number is required.' }));
            return false;
        } else if (phoneDigits.length < 10) {
            setFormErrors(prevErrors => ({ ...prevErrors, cell: 'Phone number must be at least 10 digits.' }));
            return false;
        }
        return true;
    }

    const validateShippingZipCode = () => {
        const zipRegex = /^\d{5}(-\d{4})?$/;
        if (!formData.shippingZipCode || formData.shippingZipCode.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, shippingZipCode: 'Zip code is required.' }));
            return false;
        } else if (!zipRegex.test(formData.shippingZipCode)) {
            setFormErrors(prevErrors => ({ ...prevErrors, shippingZipCode: 'Invalid zip code format.' }));
            return false;
        }
        return true;
    }

    const validateShippingAddressLine1 = () => {
        if (!formData.shippingAddressLine1 || formData.shippingAddressLine1.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, shippingAddressLine1: 'Street address is required.' }));
            return false;
        }

        // Validate if there's at least two letters and a number
        const addressLine1 = formData.shippingAddressLine1.trim();
        const letterCount = (addressLine1.match(/[a-zA-Z]/g) || []).length;
        const numberCount = (addressLine1.match(/[0-9]/g) || []).length;

        if (letterCount < 2 || numberCount === 0) {
            setFormErrors(prevErrors => ({ ...prevErrors, shippingAddressLine1: 'Street address must contain at least two letters and one number.' }));
            return false;
        }

        if (isPOBox(formData.shippingAddressLine1)) {
            setFormErrors(prevErrors => ({ ...prevErrors, shippingAddressLine1: 'PO Box addresses are not allowed.' }));
            return false;
        }

        return true;
    }

    const validateShippingAddressLine2 = () => {
        if (formData.shippingAddressLine2 && isPOBox(formData.shippingAddressLine2)) {
            setFormErrors(prevErrors => ({ ...prevErrors, shippingAddressLine2: 'PO Box addresses are not allowed.' }));
            return false;
        }
        return true;
    }

    const validateShippingCity = () => {
        if (!formData.shippingCity || formData.shippingCity.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, shippingCity: 'City is required.' }));
            return false;
        }
        return true;
    }

    const validateShippingState = () => {
        if (!formData.shippingState || formData.shippingState.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, shippingState: 'State is required.' }));
            return false;
        }
        return true;
    }

    const validateBillingZipCode = () => {
        const zipRegex = /^\d{5}(-\d{4})?$/;
        if (!formData.billingZipCode || formData.billingZipCode.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, billingZipCode: 'Zip code is required.' }));
            return false;
        } else if (!zipRegex.test(formData.billingZipCode)) {
            setFormErrors(prevErrors => ({ ...prevErrors, billingZipCode: 'Invalid zip code format.' }));
            return false;
        }
        return true;
    }

    const validateBillingCity = () => {
        if (!formData.billingCity || formData.billingCity.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, billingCity: 'City is required.' }));
            return false;
        }
        return true;
    }

    const validateBillingState = () => {
        if (!formData.billingState || formData.billingState.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, billingState: 'State is required.' }));
            return false;
        }
        return true;
    }

    const validateBillingAddressLine1 = () => {
        if (!formData.billingAddressLine1 || formData.billingAddressLine1.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, billingAddressLine1: 'Street address is required.' }));
            return false;
        }

        if (isPOBox(formData.billingAddressLine1)) {
            setFormErrors(prevErrors => ({ ...prevErrors, billingAddressLine1: 'PO Box addresses are not allowed.' }));
            return false;
        }

        return true;
    }

    const validateBillingAddressLine2 = () => {
        if (formData.billingAddressLine2 && isPOBox(formData.billingAddressLine2)) {
            setFormErrors(prevErrors => ({ ...prevErrors, billingAddressLine2: 'PO Box addresses are not allowed.' }));
            return false;
        }
        return true;
    }

    const validateCardholderName = () => {
        if (!formData.cardholderName || formData.cardholderName.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, cardholderName: 'Cardholder name is required.' }));
            return false;
        }
        return true;
    }

    // Helper function to limit phone input to digits only
    const handlePhoneKeyDown = (e) => {
        // Allow backspace, delete, tab, escape, enter, and arrow keys
        if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode) ||
            // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.keyCode === 65 && e.ctrlKey) ||
            (e.keyCode === 67 && e.ctrlKey) ||
            (e.keyCode === 86 && e.ctrlKey) ||
            (e.keyCode === 88 && e.ctrlKey)) {
            return;
        }
        
        // Only allow digits
        if ((e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
            e.preventDefault();
        }
    };

    // Helper function to detect valid zip codes
    const handleZipCodeKeyDown = (e) => {
        // Allow backspace, delete, tab, escape, enter, and arrow keys
        if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode) ||
            // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.keyCode === 65 && e.ctrlKey) ||
            (e.keyCode === 67 && e.ctrlKey) ||
            (e.keyCode === 86 && e.ctrlKey) ||
            (e.keyCode === 88 && e.ctrlKey)) {
            return;
        }

        // Only allow digits
        if ((e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
            e.preventDefault();
        }
    };

    // Helper function to detect PO Box addresses
    const isPOBox = (addressLine) => {
        if (!addressLine) return false;
        const normalizedAddress = addressLine.toLowerCase().trim();
        const poBoxPatterns = [
            /^p\.?o\.?\s*box/i,
            /^post\s*office\s*box/i,
            /^po\s*box/i,
            /^p\s*o\s*box/i,
            /^pob\s/i,
            /^pmb\s/i, // Private Mail Box
            /^hc\s*\d+\s*box/i, // Highway Contract
            /^rural\s*route/i,
            /^rr\s*\d+/i,
            /^box\s*\d+/i
        ];
        return poBoxPatterns.some(pattern => pattern.test(normalizedAddress));
    };

    // Phone formatting helper
    const formatPhoneDisplay = (value) => {
        if (!value) return '';
        const digits = value.replace(/\D/g, '');
        
        if (digits.length >= 10) {
            return `(${digits.substr(0, 3)}) ${digits.substr(3, 3)}-${digits.substr(6, 4)}`;
        } else if (digits.length >= 6) {
            return `(${digits.substr(0, 3)}) ${digits.substr(3, 3)}-${digits.substr(6)}`;
        } else if (digits.length >= 3) {
            return `(${digits.substr(0, 3)}) ${digits.substr(3)}`;
        } else if (digits.length > 0) {
            return `(${digits}`;
        }
        return '';
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value;
        const digits = value.replace(/\D/g, '');
        const limitedDigits = digits.substr(0, 10);
        const formattedValue = formatPhoneDisplay(limitedDigits);
        
        setPhoneDisplayValue(formattedValue);
        setFormData(prevData => ({
            ...prevData,
            cell: limitedDigits
        }));

        localStorage.setItem('checkout_form_data', JSON.stringify({
            ...formData,
            cell: limitedDigits
        }));

    };

    const handleSendGooglePurchaseEvent = (plan, paymentIntentId) => {
        // Currently using GTM:
        if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'conversion',              // Trigger in GTM
                send_to: 'AW-17936828543/Q93XCMPd-vYbEP-Q-ehC',
                value: parseFloat(plan.total),
                currency: 'USD',
                transaction_id: paymentIntentId, // Using payment intent ID as transaction ID for tracking
                all_query_params: JSON.parse(localStorage.getItem('allQueryParams') || '{}'), // Pass all query parameters for better attribution
                utm_source: source,
            });
        }
    };

    const handleSendMetaPurchaseEvent = (plan) => {
        if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
            window.fbq('track', 'Purchase', {
                value: parseFloat(plan.total),
                currency: 'USD',
            });
        }
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();

        // The buttons are disabled for this case, but that is only markup — guard
        // the handler too so the order cannot be forced through from devtools.
        if (sublingualBlocked) {
            return;
        }

        if (!formData.telemedicineOptin) {
            return;
        }
        
        // Validate all fields before submission

        // Validate contact info
        const isFirstNameValid = validateFirstName();
        const isLastNameValid = validateLastName();
        const isEmailValid = validateEmail();
        const isPhoneValid = validatePhone();

        const contactInfoValid = isFirstNameValid && isLastNameValid && isEmailValid && isPhoneValid;

        if (!contactInfoValid) {
            contactInfoRef.current.scrollIntoView({ behavior: 'smooth' });
            return;
        }

        // Validate payment info
        const isCardholderNameValid = validateCardholderName();

        if (!isCardholderNameValid) {
            paymentInfoRef.current.scrollIntoView({ behavior: 'smooth' });
            return;
        }

        // Validate shipping address
        const isShippingAddressLine1Valid = validateShippingAddressLine1();
        const isShippingAddressLine2Valid = validateShippingAddressLine2();
        const isShippingCityValid = validateShippingCity();
        const isShippingStateValid = validateShippingState();
        const isShippingZipCodeValid = validateShippingZipCode();

        const shippingAddressValid = isShippingAddressLine1Valid && isShippingAddressLine2Valid && isShippingCityValid && isShippingStateValid && isShippingZipCodeValid;
        if (!shippingAddressValid) {
            shippingAddressRef.current.scrollIntoView({ behavior: 'smooth' });
            return;
        }

        // Validate billing address if not same as shipping
        let isBillingAddressValid = true;

        if (!formData.sameAsShipping) {
            const isBillingAddressLine1Valid = validateBillingAddressLine1();
            const isBillingAddressLine2Valid = validateBillingAddressLine2();
            const isBillingCityValid = validateBillingCity();
            const isBillingStateValid = validateBillingState();
            const isBillingZipCodeValid = validateBillingZipCode();
            isBillingAddressValid = isBillingAddressLine1Valid && isBillingAddressLine2Valid && isBillingCityValid && isBillingStateValid && isBillingZipCodeValid;
        }

        if (!isBillingAddressValid) {
            billingAddressRef.current.scrollIntoView({ behavior: 'smooth' });
            return;
        }

        // If all validations pass, proceed with payment processing

        if (formData.sameAsShipping) {
            setFormData(prevData => ({
                ...prevData,
                billingAddressLine1: prevData.shippingAddressLine1,
                billingAddressLine2: prevData.shippingAddressLine2,
                billingCity: prevData.shippingCity,
                billingState: prevData.shippingState,
                billingZipCode: prevData.shippingZipCode
            }));
        }

        setIsSubmitting(true);

        // Get the selected plan details from the current planId

        const plan = pricing.getPlanById(planId);
        if (!plan) {
            console.error("Invalid plan ID selected.");
            setIsSubmitting(false);
            return;
        }

        const isTelegra = process.env.REACT_APP_TELEGRA_ENABLED === 'true';
        let telegraProductId = isTelegra ? plan.telegra_product_id : null;

        try {

            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: elements.getElement(CardElement),
                billing_details: {
                    name: formData.cardholderName,
                    email: formData.email,
                    address: {
                        line1: formData.billingAddressLine1,
                        city: formData.billingCity,
                        state: formData.billingState,
                        postal_code: formData.billingZipCode
                    }
                }
            });

            if (error) {

                console.error("Error creating payment method:", error);
                setCardErrorMessage(error.message || 'An error occurred while processing your payment. Please try again.');
                setIsSubmitting(false);
                paymentInfoRef.current.scrollIntoView({ behavior: 'smooth' });

            } else {
                
                // Get interview data
                const interviewSummary = getInterviewSummary();

                // Prepare payload for backend
                const body = {
                    paymentMethodId: paymentMethod.id,
                    email: formData.email,
                    name: formData.cardholderName,
                    // Patient name fields
                    firstName: formData.firstName,
                    middleInitial: '',
                    lastName: formData.lastName,
                    // Billing address
                    address1: formData.sameAsShipping ? formData.shippingAddressLine1 : formData.billingAddressLine1,
                    address2: formData.sameAsShipping ? formData.shippingAddressLine2 : formData.billingAddressLine2,
                    city: formData.sameAsShipping ? formData.shippingCity : formData.billingCity,
                    state: formData.sameAsShipping ? formData.shippingState : formData.billingState,
                    zipCode: formData.sameAsShipping ? formData.shippingZipCode : formData.billingZipCode,
                    // Shipping address
                    shippingAddress1: formData.shippingAddressLine1,
                    shippingAddress2: formData.shippingAddressLine2,
                    shippingCity: formData.shippingCity,
                    shippingState: formData.shippingState,
                    shippingZipCode: formData.shippingZipCode,
                    phone: formData.cell,
                    // Plan details
                    planName: plan.name,
                    planId: plan.id,
                    ...(isTelegra && { telegraProductId: telegraProductId }),
                    ...(isTelegra && { telegraActive: isTelegra }),
                    sameAsShipping: formData.sameAsShipping,
                    consentAcknowledged: formData.consentAcknowledged,
                    notificationOptin: formData.notificationOptin,
                    marketingOptin: formData.marketingOptin,
                    telemedicineOptin: formData.telemedicineOptin,
                    utmSource: source,
                    utmMedium: utmMedium,
                    utmCampaign: utmCampaign,
                    utmContent: utmContent,
                    utmTerm: utmTerm,
                    source: source,
                    subdomain: localStorage.getItem('subdomain'),
                    mcTransactionId: localStorage.getItem('mcTransactionId'),
                    coupon: coupon && coupon.id,
                    // Interview data
                    interviewData: {
                        decision: interviewSummary.decision,
                        goal: interviewSummary.goal,
                        bestYouGoal: interviewSummary.bestYouGoal,
                        heightFeet: interviewSummary.heightFeet,
                        heightInches: interviewSummary.heightInches,
                        weight: interviewSummary.weight,
                        bmi: interviewSummary.bmi,
                        bmiCategory: interviewSummary.bmiCategory,
                        priority: interviewSummary.priority,
                        frequency: interviewSummary.frequency,
                        medication: interviewSummary.medication,
                        isComplete: interviewSummary.isComplete
                    }
                }

                const response = await fetch(`${process.env.REACT_APP_TB2_API_URL}/events/create-subscription`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(body)
                });

                const result = await response.json();

                if (result.error) {

                    console.error("Backend error:", result.error);
                    setCardErrorMessage('An error occurred while processing your payment. Please try again.');
                    paymentInfoRef.current.scrollIntoView({ behavior: 'smooth' });
                    setIsSubmitting(false);
                    return;

                } else {

                    // Store payment completion info
                    sessionStorage.setItem('tb2_payment_completed', 'true');
                    sessionStorage.setItem('tb2_payment_timestamp', Date.now().toString());
                    
                    // Store plan information for success page
                    localStorage.setItem('tb2_selected_plan', JSON.stringify({
                        title: plan.title,
                        total: plan.total,
                        name: plan.name
                    }));
                    
                    // Store Telegra URL if provided
                    if (result.data?.telegra?.data?.telegraUrl) {
                        localStorage.setItem('tb2_telegra_url', result.data.telegra.data.telegraUrl);
                        localStorage.setItem('tb2_telegra_timestamp', Date.now().toString());
                    }

                    // Get payment intent ID from backend response for tracking
                    const paymentIntentId = result.data?.subscription.subscription.latest_invoice?.payment_intent?.id;

                    // Identify user in posthog
                    identifyPostHogUser(formData.email, {});

                    // Track event in Google Ads
                    handleSendGooglePurchaseEvent(plan, paymentIntentId);

                    // Track event in Meta Pixel
                    handleSendMetaPurchaseEvent(plan);

                    // If Patient Portal registrationToken is returned, navigate with React Router State; otherwise, just navigate to success page
                    if (result.data?.patientPortalRegistrationToken) {
                        navigate('/payment-success', { state: { patientPortalRegistrationToken: result.data.patientPortalRegistrationToken } });
                    } else {
                        navigate('/payment-success');
                    }
                }
            } 


        } catch (err) {
            console.error("Error creating payment method:", err);
            setCardErrorMessage('An error occurred while processing your payment. Please try again.');
            paymentInfoRef.current.scrollIntoView({ behavior: 'smooth' });
            setIsSubmitting(false);
        }
    }

    
    useEffect(() => {
        
        // Check for payment success indicators in URL or session/local storage

        const sessionId = searchParams.get('session_id');
        const orderId = searchParams.get('orderId');
        
        const recentPayment = sessionStorage.getItem('tb2_payment_completed');
        const paymentTimestamp = sessionStorage.getItem('tb2_payment_timestamp');
        
        const storedTelegraUrl = localStorage.getItem('tb2_telegra_url');
        const telegraTimestamp = localStorage.getItem('tb2_telegra_timestamp');
        
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        const isRecentPayment = paymentTimestamp && parseInt(paymentTimestamp) > thirtyDaysAgo;
        const isValidTelegraUrl = storedTelegraUrl && telegraTimestamp && parseInt(telegraTimestamp) > thirtyDaysAgo;
        
        if (sessionId || orderId || (recentPayment && isRecentPayment) || isValidTelegraUrl) {
            openPaymentWarning();
            if (isValidTelegraUrl) {
                setTelegraUrl(storedTelegraUrl);
            }
        }

        // Load form data from localStorage if there is any

        const storedFormData = localStorage.getItem('checkout_form_data');
        if (storedFormData) {
            try {
                const parsedData = JSON.parse(storedFormData);
                const mergedData = {
                    firstName: '',
                    lastName: '',
                    email: '',
                    cell: '',
                    name: '',
                    addressLine1: '',
                    addressLine2: '',
                    city: '',
                    state: '',
                    zipCode: '',
                    shippingAddressLine1: '',
                    shippingAddressLine2: '',
                    shippingCity: '',
                    shippingState: '',
                    shippingZipCode: '',
                    sameAsShipping: false,
                    notificationOptin: true,
                    telemedicineOptin: true,
                    marketingOptin: true,
                    consentAcknowledged: false,
                    ...parsedData
                };
                setFormData(mergedData);
                setPhoneDisplayValue(formatPhoneDisplay(mergedData.cell));
                if (!mergedData.telemedicineOptin) {
                    setFormErrors(prevErrors => ({
                        ...prevErrors,
                        telemedicineOptin: 'You must acknowledge telemedicine enrollment before placing your order.'
                    }));
                }
            } catch (e) {
                console.error('Error parsing stored form data:', e);
            }
        }

    }, []);

    return (
        <>
            {sublingualBlocked && <BackorderBanner />}

            <div className={styles.checkoutPageContainer}>

                <Header hideNavigation nonSticky />

                <div className={styles.checkoutPageContent}>

                    <div className={styles.checkoutLeftColumn}>

                        <div className={styles.checkoutBanner}>
                            <span className={styles.checkoutBannerText}>You're almost there!</span>
                            <h1 className={styles.checkoutBannerTitle}>Secure Checkout</h1>
                            <div className={styles.checkoutBannerInfo}>
                                <div className={styles.lockIconWrapper}>
                                    <LockOutlinedIcon fontSize='small'/>
                                </div>
                                <span>Encrypted | Powered by Stripe</span>
                            </div>
                        </div>

                        <form className={styles.checkoutForm}>

                            {/* Basic Info */}
                            <section className={styles.checkoutSection} ref={contactInfoRef}>
                                
                                <span className={styles.checkoutSectionTitle}>Your Details</span>

                                <div className={styles.formGrid}>

                                    <InputText
                                        name="firstName"
                                        value={formData.firstName}
                                        label="First Name"
                                        placeholder="First Name"
                                        onChange={handleInputChange}
                                        error={formErrors.firstName}
                                        onFocus={handleClearError}
                                        onBlur={validateFirstName}
                                    />

                                    <InputText
                                        name="lastName"
                                        value={formData.lastName}
                                        label="Last Name"
                                        placeholder="Last Name"
                                        onChange={handleInputChange}
                                        error={formErrors.lastName}
                                        onFocus={handleClearError}
                                        onBlur={validateLastName}
                                    />

                                    <InputText
                                        name="cell"
                                        value={phoneDisplayValue}
                                        onChange={handlePhoneChange}
                                        onKeyDown={handlePhoneKeyDown}
                                        maxLength={14}
                                        label="Phone Number"
                                        placeholder="(123) 456-7890"
                                        type="tel"
                                        error={formErrors.cell}
                                        onFocus={handleClearError}
                                        onBlur={validatePhone}
                                    />

                                    <InputText
                                        name="email"
                                        value={formData.email}
                                        label="Email Address"
                                        placeholder="user@email.com"
                                        type="email"
                                        onChange={handleInputChange}
                                        error={formErrors.email}
                                        onFocus={handleClearError}
                                        onBlur={validateEmail}
                                    />

                                </div>

                            </section>

                            {/* Payment Info */}
                            <section className={styles.checkoutSection } ref={paymentInfoRef}>

                                <span className={styles.checkoutSectionTitle}>Payment Information</span>

                                <div className={styles.formGrid}>

                                    <InputText
                                        label="Cardholder Name"
                                        placeholder="Cardholder Name"
                                        value={formData.cardholderName}
                                        onChange={handleInputChange}
                                        name="cardholderName"
                                        error={formErrors.cardholderName}
                                        onFocus={handleClearError}
                                        onBlur={validateCardholderName}
                                    />

                                    <div className={styles.cardInputContainer}>
                                        <label className={styles.inputLabel}>Card Details</label>
                                        <div className={`${styles.cardElementWrapper}` + (cardInputFocused ? ` ${styles.cardElementFocused}` : '') + (cardErrorMessage ? ` ${styles.cardElementError}` : '')}>
                                            <CardElement
                                                options={{
                                                        style: {
                                                            base: {
                                                                fontSize: '18px',
                                                            },
                                                        invalid: {
                                                            color: '#FF4D4D',
                                                            }
                                                        },
                                                    }}
                                                onFocus={() => setCardInputFocused(true)}
                                                onBlur={() => setCardInputFocused(false)}
                                            />
                                        </div>
                                        {cardErrorMessage && <span className={styles.cardErrorMessage}>{cardErrorMessage}</span>}
                                    </div>

                                </div>

                            </section>

                            {/* Shipping Address */}

                            <section className={styles.checkoutSection} ref={shippingAddressRef}>
                                
                                <span className={styles.checkoutSectionTitle}>Shipping Address</span>

                                    <div className={styles.formGrid}>

                                        <InputText
                                            label="Address Line 1"
                                            placeholder="Address Line 1"
                                            value={formData.shippingAddressLine1}
                                            onChange={handleInputChange}
                                            name="shippingAddressLine1"
                                            error={formErrors.shippingAddressLine1}
                                            onFocus={handleClearError}
                                            onBlur={validateShippingAddressLine1}
                                        />

                                        <InputText
                                            label="Address Line 2"
                                            placeholder="Address Line 2"
                                            value={formData.shippingAddressLine2}
                                            onChange={handleInputChange}
                                            name="shippingAddressLine2"
                                            error={formErrors.shippingAddressLine2}
                                            onFocus={handleClearError}
                                            onBlur={validateShippingAddressLine2}
                                        />

                                    </div>
                                    
                                    <div className={styles.addressRow}>

                                        <InputText
                                            label="City"
                                            placeholder="City"
                                            value={formData.shippingCity}
                                            onChange={handleInputChange}
                                            name="shippingCity"
                                            error={formErrors.shippingCity}
                                            onFocus={handleClearError}
                                            onBlur={validateShippingCity}
                                        />

                                        <InputAutocomplete
                                            label="State"
                                            value={formData.shippingState}
                                            onChange={handleInputChange}
                                            name="shippingState"
                                            placeholder="State"
                                            options={US_STATES}
                                            error={formErrors.shippingState}
                                            onFocus={handleClearError}
                                            onBlur={validateShippingState}
                                        />

                                        <InputText
                                            label="Zip Code"
                                            placeholder="Zip Code"
                                            value={formData.shippingZipCode}
                                            onKeyDown={handleZipCodeKeyDown}
                                            onChange={handleInputChange}
                                            name="shippingZipCode"
                                            error={formErrors.shippingZipCode}
                                            onFocus={handleClearError}
                                            maxLength='5'
                                            min="0"
                                            onBlur={validateShippingZipCode}
                                        />

                                    </div>

                            </section>

                            {/* Billing Address */}

                            <section className={styles.checkoutSection} ref={billingAddressRef}>

                                <span className={styles.checkoutSectionTitle}>Billing Address</span>

                                <div className={styles.checkboxContainer}>
                                    <GradientCheckBox
                                        checked={formData.sameAsShipping}
                                        onChange={() => setFormData(prevData => ({ ...prevData, sameAsShipping: !prevData.sameAsShipping }))}
                                    />
                                    <span className={styles.checkboxLabel}>Billing address is the same as shipping</span>
                                </div>

                                {!formData.sameAsShipping && (
                                    <>
                                        <div className={styles.formGrid}>

                                            <InputText
                                                label="Address Line 1"
                                                placeholder="Address Line 1"
                                                value={formData.billingAddressLine1}
                                                onChange={handleInputChange}
                                                name="billingAddressLine1"
                                                error={formErrors.billingAddressLine1}
                                                onFocus={handleClearError}
                                                onBlur={validateBillingAddressLine1}
                                            />

                                            <InputText
                                                label="Address Line 2"
                                                placeholder="Address Line 2"
                                                value={formData.billingAddressLine2}
                                                onChange={handleInputChange}
                                                name="billingAddressLine2"
                                                error={formErrors.billingAddressLine2}
                                                onFocus={handleClearError}
                                                onBlur={validateBillingAddressLine2}
                                            />

                                        </div>
                                        
                                        <div className={styles.addressRow}>

                                            <InputText
                                                label="City"
                                                placeholder="City"
                                                value={formData.billingCity}
                                                onChange={handleInputChange}
                                                name="billingCity"
                                                error={formErrors.billingCity}
                                                onFocus={handleClearError}
                                                onBlur={validateBillingCity}
                                            />

                                            <InputAutocomplete
                                            label="State"
                                            value={formData.billingState}
                                            onChange={handleInputChange}
                                            name="billingState"
                                            placeholder="State"
                                            options={US_STATES}
                                            error={formErrors.billingState}
                                            onFocus={handleClearError}
                                            onBlur={validateBillingState}
                                        />

                                            <InputText
                                                label="Zip Code"
                                                placeholder="Zip Code"
                                                value={formData.billingZipCode}
                                                onChange={handleInputChange}
                                                name="billingZipCode"
                                                error={formErrors.billingZipCode}
                                                onFocus={handleClearError}
                                                onKeyDown={handleZipCodeKeyDown}
                                                maxLength="5"
                                                min="0"
                                                onBlur={validateBillingZipCode}
                                            />

                                        </div>
                                    </>
                                )}

                            </section>

                            {/* Consent section */}
                            <section className={styles.checkoutSection}>

                                <div className={styles.consentCheckboxContainer}>
                                    <Checkbox
                                        name="notificationOptin"
                                        checked={formData.notificationOptin}
                                        onChange={(e) => handleInputChange(e)}
                                    />
                                    <span className={styles.consentCheckboxLabel}>I consent to receive transactional messages from TB2.health regarding my order, including consultation updates, prescription status, and shipping notifications. Message frequency may vary. Message and data rates may apply. I can reply HELP for help or STOP to opt out.</span>
                                </div>

                                {formErrors.telemedicineOptin && (
                                    <div className={styles.cardErrorMessage}>{formErrors.telemedicineOptin}</div>
                                )}
                                <div className={styles.consentCheckboxContainer}>
                                    <Checkbox
                                        name="telemedicineOptin"
                                        checked={formData.telemedicineOptin}
                                        onChange={(e) => handleInputChange(e)}
                                    />
                                    <span className={styles.consentCheckboxLabel}>I acknowledge that I am enrolling in a telehealth-enabled program. I understand that payment is collected at checkout, prescriptions are not guaranteed, and clinical decisions are made independently by licensed healthcare providers. If I am not approved for treatment, I will receive a full refund.</span>
                                </div>

                            </section>

                            {/* Submit Button */}
                            <div className={styles.placeOrderButtonContainer}>
                                <div className={styles.consentCheckboxContainer}>
                                    <span className={styles.consentCheckboxLabel}>
                                        By clicking <b>"Place Order"</b> you agree to our{' '}
                                        <Link to='/terms' className={styles.termsAndPrivacyLink} target="_blank" rel="noopener noreferrer">Terms of Service</Link>{' '}
                                        and{' '}
                                        <Link to='/privacy' className={styles.termsAndPrivacyLink} target="_blank" rel="noopener noreferrer">Privacy Policy</Link>, and authorize TB2.health to charge your payment method for this subscription and any future recurring payments in accordance with those terms. You may cancel at any time.
                                    </span>
                                </div>
                                {isSubmitting ? (
                                    <CircularProgress />
                                ) : (
                                    <button
                                        className={styles.placeOrderButton}
                                        onClick={handlePaymentSubmit}
                                        type="submit"
                                        disabled={!formData.telemedicineOptin || sublingualBlocked}
                                    >
                                        Place Order
                                    </button>
                                )}                                
                            </div>

                        </form>

                    </div>

                    <div className={styles.checkoutRightColumn}>
                        <div className={styles.subscriptionInfoContainer}>
                            <SubscriptionInfo planId={planId} onChangeFrequency={handleChangeFrequency} initialFrequency={initialPlan?.subscription_length === '1 Month' ? 'monthly' : 'threeMonths'} />
                        </div>

                        <div className={styles.placeOrderButtonContainerMobile}>
                            <div className={styles.consentCheckboxContainer}>
                                <span className={styles.consentCheckboxLabel}>
                                    By clicking <b>"Place Order"</b> you agree to our{' '}
                                    <Link to='/terms' className={styles.termsAndPrivacyLink} target="_blank" rel="noopener noreferrer">Terms of Service</Link>{' '}
                                    and{' '}
                                    <Link to='/privacy' className={styles.termsAndPrivacyLink} target="_blank" rel="noopener noreferrer">Privacy Policy</Link>, and authorize TB2.health to charge your payment method for this subscription and any future recurring payments in accordance with those terms. You may cancel at any time.
                                </span>
                            </div>
                            {isSubmitting ? (
                                <CircularProgress />
                            ) : (
                                <button
                                    className={styles.placeOrderButton}
                                    onClick={handlePaymentSubmit}
                                    type="submit"
                                    disabled={!formData.telemedicineOptin || sublingualBlocked}
                                >
                                    Place Order
                                </button>
                            )}
                        </div>

                        <LegitScriptBanner className={styles.legitScriptBanner} />

                    </div>

                </div>

                <PaymentWarningPopUp
                    isOpen={isPaymentWarningOpen}
                    telegraUrl={telegraUrl}
                    onClose={closePaymentWarning}
                />

                <NonWholesaleStateWarningPopUp
                    isOpen={isStateWarningOpen}
                    stateName={US_STATES.find(s => s.value === restrictedStateAbbr)?.label || restrictedStateAbbr}
                    onConfirm={handleStateWarningConfirm}
                    onCancel={handleStateWarningCancel}
                />

            </div>
            <Footer />
        </>
    );
}

// Wrapper component for the checkout to include Stripe Elements
export default function Checkout() {
    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);
    return (
        <Elements stripe={stripePromise}>
            <CheckoutContent />
        </Elements>
    )
}